import { Badge, Logo } from '@artosera/ui';
import { can, type Sujet } from '@artosera/shared';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router';
import type { GalerieSession } from '../../api/types.js';
import { galerieAModule } from '../../auth/sujet.js';
import { Cadenas } from '../module/LectureSeule.js';
import { entreeEnLectureSeule, entreesVisibles, ENTREE_GROUPE } from './navigation.js';
import styles from './HorizontalNav.module.css';

export interface HorizontalNavProps {
  galerie: GalerieSession;
  galerieSlug: string;
  sujetGroupe: Sujet | null;
  modulesAVenir?: number;
  nonLues?: number;
}
/** Same permission registry as the sidebar; a different layout never grants access. */
export function HorizontalNav({
  galerie,
  galerieSlug,
  sujetGroupe,
  modulesAVenir = 0,
  nonLues = 0,
}: HorizontalNavProps): JSX.Element {
  const { t, i18n } = useTranslation(['commun', 'regie']);
  const groupeVisible =
    sujetGroupe !== null &&
    can(sujetGroupe, 'group:read') &&
    galerieAModule(galerie, 'console-groupe');
  const label = (i18n.resolvedLanguage ?? i18n.language).startsWith('en')
    ? 'Main navigation'
    : 'Navigation principale';
  return (
    <nav className={styles.navigation} data-ui="horizontal-navigation" aria-label={label}>
      <NavLink to={`/${galerieSlug}`} end className={styles.brand} data-ui="brand">
        <Logo title={t('commun:produit.nom')} />
      </NavLink>
      <ul className={styles.links} data-ui="horizontal-links">
        {entreesVisibles(galerie).map((entry) => {
          const lectureSeule = entreeEnLectureSeule(galerie, entry);
          return (
            <li key={entry.cle}>
              <NavLink
                to={entry.chemin ? `/${galerieSlug}/${entry.chemin}` : `/${galerieSlug}`}
                end={entry.chemin === ''}
                data-ui="nav-link"
                data-nav={entry.cle}
                {...(lectureSeule
                  ? {
                      'data-lecture-seule': 'true',
                      'aria-label': t('regie:module.lectureSeule.entree', {
                        libelle: t(entry.libelleNavigation),
                      }),
                      title: t('regie:module.lectureSeule.etiquette'),
                    }
                  : {})}
                className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
              >
                <span>{t(entry.libelleNavigation)}</span>
                {lectureSeule ? <Cadenas size={12} /> : null}
                {entry.cle === 'boite-reception' && nonLues > 0 ? (
                  <Badge aria-label={t('regie:boiteReception.nonLues', { count: nonLues })}>
                    {nonLues}
                  </Badge>
                ) : null}
              </NavLink>
            </li>
          );
        })}
        {modulesAVenir > 0 ? (
          <li>
            <Link
              to={`/${galerieSlug}#carte-des-modules`}
              data-ui="nav-link"
              data-nav="modules"
              className={styles.link}
              aria-label={`${t('regie:modulesAVenir.navigation.libelle')}, ${t(
                'regie:modulesAVenir.navigation.pastille',
                { count: modulesAVenir },
              )}`}
            >
              <span>{t('regie:modulesAVenir.navigation.libelle')}</span>
              <span className={styles.pastille} aria-hidden="true">
                {modulesAVenir}
              </span>
            </Link>
          </li>
        ) : null}
        {groupeVisible ? (
          <li>
            <NavLink
              to={`/${galerieSlug}/${ENTREE_GROUPE.chemin}`}
              data-ui="nav-link"
              data-nav="groupe"
              className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
            >
              <span>{t(ENTREE_GROUPE.libelleNavigation)}</span>
            </NavLink>
          </li>
        ) : null}
      </ul>
    </nav>
  );
}
